import React, { useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  TextField,
  FormLabel,
  FormControl,
  Button,
  Grid,
  Paper,
  Typography,
  Divider,
  InputAdornment,
} from "@mui/material";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { requestPost } from "../../config/Api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";

const SignInForm = () => {
  const { login } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const newErrors = {};
    const emailRegex = /\S+@\S+\.\S+/;

    if (!formData.email || !emailRegex.test(formData.email)) {
      newErrors.email = "Por favor, ingresa un email válido.";
    }

    if (!formData.password) {
      newErrors.password = "La contraseña es obligatoria.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (loading) return;
    if (!validate()) return;

    setLoading(true);
    try {
      const response = await requestPost("login", formData);
      const { token, user, expires_at } = response?.data || {};

      if (token && user) {
        login(token, user, expires_at);
        navigate("/home", { replace: true });
        return;
      }

      showToast("Respuesta inválida del servidor.", "error")
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;

      if (status === 422) {
        if (data?.errors) {
          const backendErrors = {};

          Object.keys(data.errors).forEach((key) => {
            backendErrors[key] = data.errors[key][0];
          });

          setErrors((prev) => ({
            ...prev,
            ...backendErrors,
          }));

          showToast(data?.message || "Revisá los datos ingresados.", "error")
        } else {
          showToast(data?.message || "Revisá los datos ingresados.", "error")
        }

        return;
      }

      if (status === 401) {
        showToast(data?.message || "Usuario o contraseña incorrectos.", "error")
        return;
      }

      showToast(
        data?.message || "Error al iniciar sesión. Intenta nuevamente.",
      "error")
    } finally {
      setLoading(false);
    }
  };

  const glassCardSx = useMemo(
    () => ({
      width: "100%",
      maxWidth: 520,
      mx: "auto",
      borderRadius: 4,
      border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
      background: isDark ? "rgba(15,23,42,0.88)" : "rgba(255,255,255,0.78)",
      backdropFilter: "blur(14px)",
      WebkitBackdropFilter: "blur(14px)",
      boxShadow: "0 18px 48px rgba(0,0,0,0.08)",
      overflow: "hidden",
    }),
    [isDark],
  );

  const headerIconWrapSx = useMemo(
    () => ({
      width: 64,
      height: 64,
      borderRadius: 3,
      display: "grid",
      placeItems: "center",
      background: isDark ? "rgba(37,99,235,0.12)" : "rgba(255,255,255,0.72)",
      border: "1px solid rgba(37,99,235,0.22)",
      boxShadow: "0 10px 26px rgba(0,0,0,0.10)",
      backdropFilter: "blur(12px)",
      WebkitBackdropFilter: "blur(12px)",
      overflow: "hidden",
    }),
    [isDark],
  );

  const inputSx = useMemo(
    () => ({
      "& .MuiOutlinedInput-root": {
        borderRadius: 3,
        background: isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.86)",
        transition: "all .16s ease",
        "& fieldset": { borderColor: isDark ? "rgba(255,255,255,0.14)" : "rgba(0,0,0,0.14)" },
        "&:hover fieldset": { borderColor: "rgba(37,99,235,0.45)" },
        "&.Mui-focused fieldset": { borderColor: "rgba(37,99,235,0.65)" },
      },
      "& .MuiInputAdornment-root": { opacity: 0.8 },
    }),
    [isDark],
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        px: 2,
        py: 4,
      }}
    >
      <Paper elevation={0} sx={glassCardSx}>
        <Box sx={{ px: 3, pt: 3, pb: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box sx={headerIconWrapSx}>
              <LockRoundedIcon
                sx={{
                  fontSize: 34,
                  color: "primary.main",
                }}
              />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: 18.5,
                  fontWeight: 950,
                  lineHeight: 1.15,
                }}
              >
                Iniciar sesión
              </Typography>
              <Typography
                variant="body2"
                sx={{ opacity: 0.7, mt: 0.3, maxWidth: 360 }}
              >
                Ingresá con tu correo y contraseña para acceder al sistema.
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ opacity: 0.7 }} />

        <Box sx={{ px: 3, py: 2.5 }}>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <FormLabel
                    htmlFor="email"
                    sx={{
                      fontWeight: 800,
                      mb: 0.7,
                      color: isDark ? "rgba(255,255,255,0.72)" : "rgba(0,0,0,0.72)",
                    }}
                  >
                    Correo
                  </FormLabel>
                  <TextField
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="tu@email.com"
                    required
                    fullWidth
                    autoComplete="email"
                    variant="outlined"
                    error={!!errors.email}
                    helperText={errors.email}
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailRoundedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormControl fullWidth>
                  <FormLabel
                    htmlFor="password"
                    sx={{
                      fontWeight: 800,
                      mb: 0.7,
                      color: isDark ? "rgba(255,255,255,0.72)" : "rgba(0,0,0,0.72)",
                    }}
                  >
                    Contraseña
                  </FormLabel>
                  <TextField
                    id="password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                    fullWidth
                    autoComplete="current-password"
                    variant="outlined"
                    error={!!errors.password}
                    helperText={errors.password}
                    sx={inputSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockRoundedIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  endIcon={!loading ? <ArrowForwardRoundedIcon /> : null}
                  sx={{
                    mt: 0.5,
                    py: 1.35,
                    borderRadius: 999,
                    fontWeight: 950,
                    letterSpacing: 0.2,
                    boxShadow: "0 14px 34px rgba(25,118,210,0.25)",
                    textTransform: "none",
                    "&:hover": {
                      boxShadow: "0 16px 38px rgba(25,118,210,0.30)",
                    },
                  }}
                >
                  {loading ? "Ingresando..." : "Ingresar"}
                </Button>

                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    textAlign: "center",
                    mt: 1.2,
                    opacity: 0.65,
                  }}
                >
                  Si tenés problemas de acceso, pedí a un admin que te
                  restablezca la contraseña.
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Divider sx={{ opacity: 0.7 }} />
        <Box sx={{ px: 3, py: 1.6, display: "flex", justifyContent: "center" }}>
          <Typography variant="caption" sx={{ opacity: 0.6 }}>
            © {new Date().getFullYear()} JEX
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default SignInForm;
